import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingFocusManager,
  hide,
  offset,
  ReferenceType,
  shift,
  useFloating
} from '@floating-ui/react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CollaborateTaskData } from 'types/api/collaborate';
import { ResponsiveDialog } from 'ui';
import { getHostAndPath, getUrlParams, isInIframe } from 'utils/proxyUtils';
import { useResponsive } from 'utils/useMediaQuery';
import { useFloatingUIManager } from '../../hooks/use-floating-manager';
import { useTaskClustering } from '../../hooks/use-task-clustering';
import { FileUploadIcon } from '../../icons/FileUploadIcon';
import { useAppDispatch, useAppSelector } from '../../store';
import { getActiveTask } from '../../store/presenceSlice';
import { getAiTaskData, getSelectedSiteId } from '../../store/siteInfoSlice';
import {
  getCreateModalOpen,
  getLoadingAIComments,
  getSelectedTaskId,
  getTasks,
  setGeneralTaskModalOpen,
  setSelectedTaskId
} from '../../store/tasksSlice';
import { getUser } from '../../store/userSlice';
import { ConvertedAiTask, isAiTask, TaskWithAi } from '../../types/ai-task';
import StackingComponent from '../stacking-tasks';
import { AiSticker } from '../sticker/ai-sticker';
import { ClusterSticker } from '../sticker/cluster-sticker';
import { Sticker } from '../sticker/sticker';
import { Task } from './create-modal/task';

export const Stickers = () => {
  // ===== HOOKS AND BASIC STATE =====
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const { closeTaskPanel } = useFloatingUIManager();
  const userInfo = useAppSelector(getUser);
  const [files, setFiles] = useState<File[]>([]);
  const [comment, setComment] = useState<string>('');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const clickArrowRef = useRef<SVGSVGElement>(null);

  // ===== SELECTORS =====
  const siteId = useSelector(getSelectedSiteId);
  const selectedTaskId = useAppSelector(getSelectedTaskId);
  const loadingAiComments = useAppSelector(getLoadingAIComments);
  const aiTaskData = useAppSelector(getAiTaskData);
  const tasks = useAppSelector(getTasks);
  const activeTask = useAppSelector(getActiveTask);
  const showTaskPanel = useAppSelector(getCreateModalOpen);

  // ===== DERIVED STATE =====
  const searchParams = getUrlParams();
  const getInitialTasks = searchParams.get('new') === 'true' ||
    (searchParams.get('new') === '1' && !userInfo?.loggedIn);
  // Track current reference setter
  const currentReferenceSetterRef = useRef<string | null>(null);



  // ===== FLOATING UI SETUP =====
  const {
    refs,
    floatingStyles,
    context,
    placement,
    middlewareData
  } = useFloating({
    open: !!showTaskPanel,
    middleware: [
      offset(8),
      flip({
        padding: 16,
        fallbackPlacements: ['left', 'top', 'bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 16 }),
      arrow({ element: clickArrowRef, padding: 16 }),
      hide(),
    ],
    placement: 'right',
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        elementResize: false,
      });
      return cleanup;
    },
  });

  // ===== FILE UPLOAD SETUP =====
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, [setFiles]);

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    noClick: true,
    onDrop,
  });

  // ===== TASK FILTERING AND PROCESSING =====
  // Create tracked reference setter
  const createTrackedSetReference = useCallback((taskId: string) => {
    return (node: ReferenceType | null) => {
      if (node) {
        if (taskId === selectedTaskId || taskId === showTaskPanel) {
          currentReferenceSetterRef.current = taskId;
          refs.setReference(node);
        }
      } else if (currentReferenceSetterRef.current === taskId) {
        currentReferenceSetterRef.current = null;
        refs.setReference(null);
      }
    };
  }, [selectedTaskId, showTaskPanel, refs]);

  const isTaskOnCurrentPage = useCallback((task: CollaborateTaskData) => {
    if (getInitialTasks) return true;

    try {
      const pageResult = getHostAndPath(window.location.href);
      if (!pageResult) return false;

      const taskResult = getHostAndPath(task.page_url);
      if (!taskResult) return false;

      if (isInIframe()) {
        return pageResult.path === taskResult.path;
      }

      return pageResult.host === taskResult.host && pageResult.path === taskResult.path;
    } catch (_e) {
      return false;
    }
  }, [getInitialTasks]);

  const filteredTasks = useMemo(() => {
    if (!tasks) return [];

    const filtered = tasks.filter((t) => {
      // Always include optimistic/pending tasks
      if (t.task_id.includes('OPTIMISTIC') || t.task_id.includes('PENDING')) {
        return true;
      }

      // Filter collaborate tasks by page and exclude general tasks
      if ('task_type' in t) {
        const collaborateTask = t as CollaborateTaskData;
        const isOnCurrentPage = isTaskOnCurrentPage(collaborateTask);
        const isNotGeneral = !(Array.isArray(collaborateTask.task_type)
          ? collaborateTask.task_type.some(type => type.toLowerCase() === 'general')
          : (collaborateTask.task_type as string).toLowerCase() === 'general');
        return isOnCurrentPage && isNotGeneral;
      }

      return false;
    });

    return filtered;
  }, [tasks, isTaskOnCurrentPage]);

  // Convert AI tasks to clusterable format
  const convertedAiTasks = useMemo((): { filteredAiTasks: ConvertedAiTask[], generalTasks: ConvertedAiTask[] } => {
    if (!aiTaskData) return { filteredAiTasks: [], generalTasks: [] };

    // filter out ai tasks with empty element path as general tasks
    const filteredAiTasks = aiTaskData.filter((aiTask) => aiTask.element?.path.trim() !== '');
    const generalTasks = aiTaskData.filter((aiTask) => aiTask.element?.path.trim() === '');
    return {
      filteredAiTasks: filteredAiTasks.map((aiTask, index) => ({
        task_comment_id: -(index + 1), // Negative IDs to avoid conflicts
        task_id: `AI_TASK_${aiTask.id}`,
        task_status: 'open' as const,
        task_priority: 'medium' as const,
        is_internal: false,
        task_element_path: aiTask.element?.path || '',
        aiTaskData: aiTask,
        isAiTask: true,
      })),
      generalTasks: generalTasks.map((aiTask, index) => ({
        task_comment_id: -(index + 1), // Negative IDs to avoid conflicts
        task_id: `GENERAL_TASK_${aiTask.id}`,
        task_status: 'open' as const,
        task_priority: 'medium' as const,
        is_internal: false,
        task_element_path: aiTask.element?.path || '',
        aiTaskData: aiTask,
        isAiTask: true,
      }))
    } as { filteredAiTasks: ConvertedAiTask[], generalTasks: ConvertedAiTask[] }
  }, [aiTaskData]);

  // Combine all tasks for clustering
  const allTasks = useMemo((): TaskWithAi[] => {
    return [...filteredTasks, ...convertedAiTasks.filteredAiTasks];
  }, [filteredTasks, convertedAiTasks]);

  // ===== CLUSTERING LOGIC =====
  const {
    clusteredData,
    expandedClusters,
    toggleCluster,
    collapseAllClusters,
    shouldAnimateCluster,
    markClusterAnimationComplete,
    isProcessing: _isProcessing
  } = useTaskClustering(allTasks);

  // ===== UTILITY FUNCTIONS =====
  const findTaskCluster = useCallback((taskId: string) => {
    return clusteredData.clusters.find(cluster =>
      cluster.tasks.some(task => task.task_id === taskId)
    );
  }, [clusteredData.clusters]);

  const getStableKey = useCallback((task: TaskWithAi, index?: number) => {
    if (isAiTask(task)) {
      return `ai-task-${task.task_id}`;
    }

    if (task.task_id.includes('OPTIMISTIC') || task.task_id.includes('PENDING')) {
      return `task-${task.task_id}`;
    }

    if (task.task_comment_id) {
      return `task-${task.task_comment_id}${index !== undefined ? `-${index}` : ''}`;
    }

    return `task-${task.task_id}${index !== undefined ? `-${index}` : ''}`;
  }, []);

  const getTransformOrigin = (placement: string = 'top'): string => {
    const side = placement.split('-')[0];
    switch (side) {
      case 'top': return 'bottom center';
      case 'bottom': return 'top center';
      case 'left': return 'right center';
      case 'right': return 'left center';
      default: return 'center center';
    }
  };

  // ===== EVENT HANDLERS =====
  const handleClusterToggle = useCallback((clusterId: string) => {
    const isCurrentlyExpanded = expandedClusters.has(clusterId);

    if (isCurrentlyExpanded) {
      // Closing a cluster - clear selection if it contains the selected task
      if (selectedTaskId) {
        const selectedTaskCluster = findTaskCluster(selectedTaskId);
        if (selectedTaskCluster && selectedTaskCluster.id === clusterId) {
          dispatch(setSelectedTaskId(null));
        }
      }
    } else {
      // Opening a cluster - clear selection if it's from a different context
      if (selectedTaskId) {
        const selectedTaskCluster = findTaskCluster(selectedTaskId);
        if (selectedTaskCluster && selectedTaskCluster.id !== clusterId) {
          dispatch(setSelectedTaskId(null));
        } else if (!selectedTaskCluster) {
          dispatch(setSelectedTaskId(null));
        }
      }
    }

    toggleCluster(clusterId);
  }, [expandedClusters, selectedTaskId, findTaskCluster, toggleCluster, dispatch]);

  // ===== EFFECTS =====
  // Initialize first task on initial load
  useEffect(() => {
    if (getInitialTasks && tasks?.length > 0) {
      const firstTask = tasks.find(t => t.task_comment_id === 1);
      if (firstTask && !selectedTaskId) {
        dispatch(setSelectedTaskId(firstTask.task_id));
      }
    }
  }, [getInitialTasks, dispatch, tasks, selectedTaskId]);

  // Manage cluster expansion based on selected task
  useEffect(() => {
    if (!selectedTaskId) return;

    const taskCluster = findTaskCluster(selectedTaskId);

    if (taskCluster) {
      const isClusterExpanded = expandedClusters.has(taskCluster.id);

      if (!isClusterExpanded) {
        // Task is in cluster but cluster is closed - open it
        toggleCluster(taskCluster.id);
      } else {
        // Task is in cluster and cluster is open - close other clusters
        const otherExpandedClusters = Array.from(expandedClusters)
          .filter(id => id !== taskCluster.id);

        if (otherExpandedClusters.length > 0) {
          collapseAllClusters();
          // Ensure timeout is cleaned up to prevent potential leaks
          const reopenTimeout = setTimeout(() => toggleCluster(taskCluster.id), 50);
          return () => clearTimeout(reopenTimeout);
        }
      }
    } else {
      // Selected task is not in any cluster - close all clusters
      if (expandedClusters.size > 0) {
        collapseAllClusters();
      }
    }
  }, [selectedTaskId, findTaskCluster, expandedClusters, collapseAllClusters, toggleCluster]);

  // ===== RENDER HELPERS =====
  const renderTaskPanel = () => {
    if (!showTaskPanel) return null;

    if (isMobile) {
      return (
        <ResponsiveDialog
          open={!!showTaskPanel}
          onOpenChange={(bool) => {
            if (!bool) {
              closeTaskPanel();
            } else {
              dispatch(setGeneralTaskModalOpen(bool));
            }
          }}
          data-atarim="true"
        >
          <ResponsiveDialog.Content
            aria-describedby={undefined}
            showClose={false}
            className="p-2 gap-2 z-20 overflow-auto bg-transparent border-none shadow-none max-h-full max-w-full"
            container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
          >
            <div
              {...getRootProps()}
              className="mx-auto w-full md:w-[400px] max-h-[684px] relative rounded-md flex flex-col gap-2"
            >
              <input {...getInputProps()} />
              {isDragActive && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background rounded-lg border-2 border-dashed border-purple-300">
                  <FileUploadIcon size={150} />
                  <p className="text-sm text-gray-600 text-center">
                    Drop your item here<br />
                    to start upload
                  </p>
                </div>
              )}
              <div className="space-y-4 bg-background px-2 py-2 rounded-lg">
                <Task fileOpen={open} files={files} setFiles={setFiles} comment={comment} setComment={setComment} />
              </div>
            </div>
          </ResponsiveDialog.Content>
        </ResponsiveDialog>
      );
    }

    return (
      <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
        <div
          data-atarim
          ref={refs.setFloating}
          id="floating-panel"
          style={{
            ...floatingStyles,
            opacity: middlewareData.hide?.referenceHidden ? 0 : 1,
            visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            zIndex: 50,
            transition: `opacity 0.2s ease-out,
            ${showTaskPanel ? 'transform 0.2s ease-out' : 'transform 0s ease-out'}`
          }}
          aria-label={t('task_panel.aria.floating_panel', 'Task details panel')}
        >
          <motion.div
            style={{ transformOrigin: getTransformOrigin(placement) }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: 'easeInOut',
              scale: {
                type: 'spring',
                stiffness: 300,
                damping: 25
              }
            }}
            className="relative pointer-events-auto bg-background w-96 p-2.5 rounded-2xl border border-border shadow-md"
          >
            <div {...getRootProps()}>
              <FloatingArrow
                ref={clickArrowRef}
                stroke="#e2e2e2"
                strokeWidth={1}
                context={context}
                className='fill-background'
              />
              <input {...getInputProps()} aria-label={t('task_panel.aria.file_input', 'File upload input')} />
              {isDragActive && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white rounded-lg border-2 border-dashed border-purple-300">
                  <FileUploadIcon size={150} aria-hidden="true" />
                  <p className="text-sm text-gray-600 text-center">
                    {t('task_panel.dropzone.title', 'Drop your item here')}<br />
                    {t('task_panel.dropzone.subtitle', 'to start upload')}
                  </p>
                </div>
              )}
              <Task fileOpen={open} files={files} setFiles={setFiles} comment={comment} setComment={setComment} />
            </div>
          </motion.div>
        </div>
      </FloatingFocusManager>
    );
  };

  // ===== EARLY RETURN =====
  if (!siteId) {
    return null;
  }

  // ===== MAIN RENDER =====
  return (
    <div
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      ref={containerRef}
    >
      {/* Individual tasks (not in clusters) - including AI tasks */}
      {clusteredData.individualTasks.map((task) => {
        if (isAiTask(task)) {

          if (task.task_element_path.trim() === '') {
            return null;
          }

          return (
            <AiSticker
              key={getStableKey(task)}
              containerRef={containerRef}
              aiTask={task.aiTaskData}
              task={task}
              positioned={true}
              inClusterPanel={false}
            />
          );
        }

        const isLoading = !!(task.task_id && loadingAiComments[task.task_id]) &&
          task.task_id !== selectedTaskId;
        const isActive = activeTask?.taskId === task.task_id &&
          task.task_id !== selectedTaskId;
        const shouldSetReference = task.task_id === selectedTaskId || task.task_id === showTaskPanel;

        return (
          <Sticker
            key={getStableKey(task)}
            containerRef={containerRef}
            draggable={!!task.task_comment_id}
            positioned={true}
            task={task}
            dummyTask={getInitialTasks}
            isActiveSticker={isActive || isLoading}
            inClusterPanel={false}
            taskPanelSetReference={
              shouldSetReference ? createTrackedSetReference(task.task_id) : undefined
            }
          />
        );
      })}

      {/* Cluster stickers with floating panels */}
      {clusteredData.clusters.map((cluster) => (
        <ClusterSticker
          key={cluster.id}
          cluster={cluster}
          onToggle={handleClusterToggle}
          hasFixedAncestor={false}
          shouldAnimateCluster={shouldAnimateCluster}
          onAnimationComplete={markClusterAnimationComplete}
          taskPanelSetReference={refs.setReference}
        />
      ))}

      {renderTaskPanel()}
      <StackingComponent items={convertedAiTasks.generalTasks} />
    </div>
  );
};